import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import {
  VCOnboardingData,
  VCOnboardingContextState,
  VCFundInfo,
  VCInvestmentFocus,
  VCIntegrations,
  VCInsights,
  VCPreferences,
} from '../types/vcOnboarding';

const VCOnboardingContext = createContext<VCOnboardingContextState | null>(null);

export const useVCOnboarding = () => {
  const context = useContext(VCOnboardingContext);
  if (!context) {
    throw new Error('useVCOnboarding must be used within VCOnboardingProvider');
  }
  return context;
};

interface VCOnboardingProviderProps {
  children: ReactNode;
}

export const VCOnboardingProvider: React.FC<VCOnboardingProviderProps> = ({ children }) => {
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [data, setData] = useState<VCOnboardingData>({
    completed: false,
    currentStep: 1,
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Load existing onboarding data on mount
  useEffect(() => {
    loadOnboardingData();
  }, []);

  const loadOnboardingData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.get('/api/vc/onboarding');
      
      if (response.data.success && response.data.data) {
        const loadedData = response.data.data;
        setData(loadedData);
        setCurrentStep(loadedData.currentStep || 1);
        
        // If already completed, redirect to dashboard
        if (loadedData.completed) {
          navigate('/vc-dashboard');
        }
      }
    } catch (err: any) {
      console.error('Error loading VC onboarding data:', err);
      setError(err.message || 'Failed to load onboarding data');
    } finally {
      setLoading(false);
    }
  };

  const saveStep = useCallback(async (step: number, stepData: any) => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiService.post('/api/vc/onboarding/save-step', {
        step,
        data: stepData,
      });

      if (response.data.success) {
        // Update local state
        const stepKey = getStepKey(step);
        setData(prev => ({
          ...prev,
          [stepKey]: stepData,
          currentStep: Math.max(prev.currentStep || 1, step),
        }));
      } else {
        throw new Error(response.data.error || 'Failed to save step');
      }
    } catch (err: any) {
      console.error(`Error saving step ${step}:`, err);
      setError(err.message || `Failed to save step ${step}`);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const nextStep = useCallback(() => {
    if (currentStep < 5) {
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep]);

  const previousStep = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const completeOnboarding = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiService.post('/api/vc/onboarding/complete');

      if (response.data.success) {
        setData(prev => ({
          ...prev,
          completed: true,
          completedAt: new Date().toISOString(),
        }));
        
        // Redirect to VC dashboard
        navigate('/vc-dashboard');
      } else {
        throw new Error(response.data.error || 'Failed to complete onboarding');
      }
    } catch (err: any) {
      console.error('Error completing onboarding:', err);
      setError(err.message || 'Failed to complete onboarding');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const value: VCOnboardingContextState = {
    currentStep,
    data,
    loading,
    error,
    saveStep,
    nextStep,
    previousStep,
    completeOnboarding,
  };

  return (
    <VCOnboardingContext.Provider value={value}>
      {children}
    </VCOnboardingContext.Provider>
  );
};

function getStepKey(step: number): keyof VCOnboardingData {
  switch (step) {
    case 1:
      return 'step1_fund_info';
    case 2:
      return 'step2_investment_focus';
    case 3:
      return 'step3_integrations';
    case 4:
      return 'step4_insights';
    case 5:
      return 'step5_preferences';
    default:
      return 'step1_fund_info';
  }
}

